import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, Tag } from "@phosphor-icons/react";

export default function Store() {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");
  const { add, count } = useCart();
  const navigate = useNavigate();

  useEffect(() => { (async () => {
    try { const { data } = await api.get("/products"); setProducts(data || []); } catch {}
  })(); }, []);

  const categories = ["All", ...Array.from(new Set(products.map((p) => p.category)))];
  const filtered = products.filter((p) =>
    (category === "All" || p.category === category) &&
    (p.name.toLowerCase().includes(search.toLowerCase()) || p.sku.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="space-y-6" data-testid="store-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Partner Store</div>
          <h1 className="font-display text-4xl font-black tracking-tight mt-1">Catalog</h1>
          <p className="text-sm text-muted-foreground mt-1">Order hardware and recurring services at partner pricing.</p>
        </div>
        <Button variant="outline" className="rounded-sm" onClick={() => navigate("/cart")} data-testid="store-view-cart">
          View cart ({count})
        </Button>
      </div>

      <Card className="p-4 rounded-sm border-border flex gap-3 flex-wrap items-center">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products or SKU"
            className="rounded-sm pl-9" data-testid="store-search" />
        </div>
        <div className="flex gap-2 flex-wrap">
          {categories.map((c) => (
            <button key={c} onClick={() => setCategory(c)} data-testid={`cat-${c}`}
              className={`text-xs uppercase tracking-[0.2em] px-3 py-2 rounded-sm border transition-colors ${
                category === c ? "border-[var(--uc-primary)] text-[var(--uc-primary)]" : "border-border text-muted-foreground hover:text-foreground"
              }`}>{c}</button>
          ))}
        </div>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {filtered.map((p) => (
          <Card key={p.product_id} className="overflow-hidden rounded-sm border-border hover:-translate-y-1 hover:shadow-lg transition-all" data-testid={`product-${p.sku}`}>
            <div className="h-44 bg-muted overflow-hidden">
              {p.image_url && <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />}
            </div>
            <div className="p-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1">
                <Tag size={10} weight="fill" /> {p.category}
              </div>
              <h3 className="font-display font-bold text-lg mt-1 leading-tight">{p.name}</h3>
              <div className="text-xs text-muted-foreground mt-1">SKU {p.sku}</div>
              <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{p.description}</p>
              <div className="mt-4 flex items-center justify-between">
                <div>
                  <div className="font-display text-2xl font-black">${p.price.toFixed(2)}</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">CAD</div>
                </div>
                <Button className="rounded-sm bg-[var(--uc-primary)] hover:bg-[var(--uc-primary-hover)] text-white"
                  onClick={() => { add(p); toast.success(`${p.name} added to cart`); }}
                  data-testid={`add-cart-${p.sku}`}>
                  <Plus size={14} className="mr-1" /> Add to cart
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
